import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from 'react';

import { isMobileScreen } from '@klp/core/app/config';
import { ParamType, getFromStorage, saveToStorage } from '@klp/utils';

interface ILayoutContext {
    menuOpen: boolean;
    menuPin: boolean;
    isMobile: boolean;
    setMenuOpen: (open: boolean) => void;
    toggleMenu: (open?: boolean) => void;
    setMenuPin: (pin: boolean) => void;
    setIsMobile: (mobile: boolean) => void;
}

const LayoutContext = createContext<ILayoutContext | undefined>(undefined);

export const useLayoutContext = () => {
    const context = useContext(LayoutContext);
    if (!context) {
        throw new Error('useLayoutContext must be used within LayoutProvider');
    }
    return context;
};

interface ILayoutProviderProps {
    children: ReactNode;
}

export const LayoutProvider: React.FC<ILayoutProviderProps> = ({
    children,
}) => {
    const [menuPin, setMenuPinState] = useState<boolean>(
        () => !!getFromStorage(ParamType.menuPin),
    );
    const [isMobile, setIsMobileState] = useState<boolean>(() =>
        isMobileScreen(),
    );
    const [menuOpen, setMenuOpenState] = useState<boolean>(() =>
        isMobileScreen() ? false : !!getFromStorage(ParamType.menuPin),
    );

    const setMenuPin = useCallback((pin: boolean) => {
        setMenuPinState(pin);
        saveToStorage(ParamType.menuPin, !!pin);
    }, []);

    const setIsMobile = useCallback(
        (mobile: boolean) => {
            setIsMobileState((prevIsMobile) => {
                if (prevIsMobile === mobile) {
                    return prevIsMobile;
                }

                // Auto-adjust menu when switching between mobile/desktop
                setMenuOpenState((_prevMenuOpen) => (mobile ? false : menuPin));

                return mobile;
            });
        },
        [menuPin],
    );

    const setMenuOpen = useCallback((open: boolean) => {
        setMenuOpenState(open);
    }, []);

    const toggleMenu = useCallback(
        (open?: boolean) => {
            setMenuOpenState((prevMenuOpen) => {
                if (isMobile) {
                    // Mobile: always toggle or set to explicit value
                    return open !== undefined ? open : !prevMenuOpen;
                }

                // Desktop with pin: keep open
                if (menuPin) {
                    return prevMenuOpen;
                }

                // Desktop without pin: toggle or set to explicit value
                return open !== undefined ? open : !prevMenuOpen;
            });
        },
        [isMobile, menuPin],
    );

    const contextValue = useMemo(
        () => ({
            menuOpen,
            menuPin,
            isMobile,
            setMenuOpen,
            toggleMenu,
            setMenuPin,
            setIsMobile,
        }),
        [
            menuOpen,
            menuPin,
            isMobile,
            setMenuOpen,
            toggleMenu,
            setMenuPin,
            setIsMobile,
        ],
    );

    return (
        <LayoutContext.Provider value={contextValue}>
            {children}
        </LayoutContext.Provider>
    );
};
